import React, { useEffect } from 'react';
import { useTenantBranding } from '../hooks/useTenantBranding-runtime';

// GoHighLevel / LeadConnector "Request Access" lead form, embedded on our own
// domain (rather than linking out) so it stays branded and in-context. Their
// form_embed.js auto-resizes the iframe to the form's content height.
const GHL_FORM_ID = 'sYJKnOWQlDk6HZBZghwD';
// Tags the lead's origin in GHL. Prefills a matching form field when the GHL
// form has one keyed `source`; harmless (ignored) otherwise.
const LEAD_SOURCE = 'portal_login';
const GHL_FORM_SRC =
  `https://api.leadconnectorhq.com/widget/form/${GHL_FORM_ID}?notrack=true&source=${encodeURIComponent(LEAD_SOURCE)}`;
const GHL_EMBED_SCRIPT = 'https://link.msgsndr.com/js/form_embed.js';

export const RequestAccess: React.FC = () => {
  const { branding } = useTenantBranding();

  // Load GHL's embed script once — it listens for the form's postMessage and
  // resizes the matching iframe to fit the content.
  useEffect(() => {
    if (document.querySelector(`script[src="${GHL_EMBED_SCRIPT}"]`)) return;
    const script = document.createElement('script');
    script.src = GHL_EMBED_SCRIPT;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <div className="min-h-screen flex flex-col justify-center px-4 py-12" style={{ background: 'var(--neutral-50)' }}>
      <div className="w-full max-w-xl mx-auto">
        {/* Brand header */}
        <div className="flex items-center justify-center space-x-3 mb-8">
          {branding.logo && (
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center p-2 shadow-lg"
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-light))' }}
            >
              <img src={branding.logo} alt={`${branding.companyName} Logo`} className="w-full h-full object-contain" />
            </div>
          )}
          <div>
            <div className="text-xl font-bold" style={{ color: 'var(--neutral-900)' }}>{branding.companyName}</div>
            <div className="text-xs" style={{ color: 'var(--neutral-500)' }}>Pharmacy Network</div>
          </div>
        </div>

        <div
          className="rounded-2xl p-6 sm:p-8 border"
          style={{ background: 'var(--surface-primary)', borderColor: 'var(--neutral-200)', boxShadow: 'var(--shadow-lg)' }}
        >
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-semibold mb-1" style={{ color: 'var(--neutral-900)' }}>Request Access</h1>
            <p className="text-sm" style={{ color: 'var(--neutral-500)' }}>
              Tell us about your practice and our team will get you set up.
            </p>
          </div>

          <iframe
            src={GHL_FORM_SRC}
            id={`inline-${GHL_FORM_ID}`}
            title="Request Access"
            style={{ width: '100%', minHeight: 640, border: 'none', borderRadius: 8 }}
            data-layout="{'id':'INLINE'}"
            data-trigger-type="alwaysShow"
            data-form-id={GHL_FORM_ID}
            data-layout-iframe-id={`inline-${GHL_FORM_ID}`}
          />
        </div>

        <div className="mt-6 text-center">
          <a href="/login" className="text-sm font-medium" style={{ color: 'var(--brand-primary)' }}>
            ← Back to sign in
          </a>
        </div>
      </div>
    </div>
  );
};

export default RequestAccess;
